import { FirebaseData } from "../General/FirebaseData";
import { UnitData } from "./Units/UnitData";
import { PropertyData } from "./PropertyData";
import { UserData } from "../User/UserData";
import { LocalData } from "../General/LocalData";
import { Role } from "../User/Role";
import { ContractorGroupData } from "../ContractorGroup/ContractorGroupData";
import { AuthError } from "firebase/auth";
import { arrayRemove } from "firebase/firestore";

export class MemberData extends FirebaseData {
  user: UserData;
  property: PropertyData;
  roles: Role[] = [];
  isAdmin: boolean = false;
  isOperationManager: boolean = false;
  units: UnitData[] = [];
  contractorGroups: ContractorGroupData[] = [];
  roleTitle: string = "";
  visibility: MemberVisibility = { hidden: false, visible: false };

  constructor(id: string, collection: string, parent: FirebaseData | undefined) {
    super(id, collection, parent);
    this.user = LocalData.users.addData(id);
    this.property = parent as PropertyData;
    this.user.addMemberData(this);
  }

  setup(data: any) {
    this.roles = Array.isArray(data.roles) ? data.roles.map((roleID: any) => Role.getRoleWithId(roleID)) : [];
    this.isAdmin = typeof data.admin === "boolean" ? data.admin : false;
    this.isOperationManager = typeof data.operationManager === "boolean" ? data.operationManager : false;
    if (super.isValidString(data.roleTitle)) {
      this.roleTitle = data.roleTitle;
    }
    if (super.isValidString(data.unit)) {
      this.addUnit(this.property.getUnitWithId(data.unit));
    }

    if (Array.isArray(data.units)) {
      this.units = [];
      for (const unitID of data.units) {
        if (super.isValidString(unitID)) {
          this.addUnit(this.property.getUnitWithId(unitID));
        }
      }
    }
    if (Array.isArray(data.contractorGroups)) {
      for (const contractorID of data.contractorGroups) {
        if (super.isValidString(contractorID)) {
          this.addContractor(LocalData.contractorGroups.addData(contractorID));
        }
      }
    }

    this.visibility = { hidden: false, visible: false };
    if (typeof data.visibility === "object") {
      this.visibility = {
        hidden: typeof data.visibility.hidden === "boolean" ? data.visibility.hidden : false,
        visible: typeof data.visibility.visible === "boolean" ? data.visibility.visible : false,
      };
    }
    super.setup(data);
  }

  isMemberOfUnit(unit: UnitData): boolean {
    return this.units.includes(unit);
  }

  hasLandlord(): boolean {
    return this.roles.includes(Role.LANDLORD);
  }

  hasTenant(): boolean {
    return this.roles.includes(Role.RENTER);
  }

  hasEntrepreneur(): boolean {
    return this.roles.includes(Role.ENTREPRENEUR);
  }

  isMemberOfMinOneUnit(units: UnitData[]): boolean {
    return this.units.some((unit) => units.includes(unit));
  }

  addUnit(unit: UnitData) {
    if (!this.units.includes(unit)) {
      this.units.push(unit);
    }
  }
  addContractor(contractor: ContractorGroupData) {
    if (!this.contractorGroups.includes(contractor)) {
      this.contractorGroups.push(contractor);
    }
  }

  removeAsLandlordMember(completion: (error?: AuthError) => void) {
    super
      .saveUpdate({
        roles: arrayRemove(1),
      })
      .then(() => {
        this.roles = this.roles.filter((role) => role !== Role.LANDLORD);
        if (this.roles.length === 0) this.deleted = true;
        completion();
      })
      .catch((error: any) => {
        completion(error);
      });
  }

  removeFromUnits(units: UnitData[], completion: (error?: Error) => void) {
    let unitIds = units.map((unit) => unit.id);
    if (unitIds.length > 0) {
      super
        .saveUpdate({
          units: arrayRemove(...unitIds),
        })
        .then(() => {
          units.forEach((unit) => {
            this.units = this.units.filter((thisUnit) => thisUnit !== unit);
          });
          if (this.units.length === 0) this.roles = this.roles.filter((role) => role !== Role.RENTER);
          if (this.roles.length === 0) this.deleted = true;
          completion();
        })
        .catch((error: Error) => {
          completion(error);
        });
    } else {
      completion(undefined);
    }
  }

  updateAdmin(admin: boolean, completion: (error?: Error) => void) {
    console.log("Updating admin to " + admin);
    super
      .saveUpdate({
        admin: admin,
      })
      .then(() => {
        completion();
      })
      .catch((error: any) => {
        completion(error);
      });
  }

  updateOperationManager(operationManager: boolean, completion: (error?: Error) => void) {
    console.log("Updating operation manager to " + operationManager);
    super
      .saveUpdate({
        operationManager: operationManager,
      })
      .then(() => {
        completion();
      })
      .catch((error: any) => {
        completion(error);
      });
  }
}

export interface MemberVisibility {
  hidden: boolean;
  visible: boolean;
}

